import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../../lib/motionShim';
import { Drumstick, ArrowRight, Zap, Info } from 'lucide-react';

const MacroCalculator = () => {
  const [formData, setFormData] = useState({
    gender: 'male',
    age: '',
    height: '',
    weight: '',
    activity: '1.55',
    goal: 'fat_loss',
  });
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateMacros = () => {
    const age = parseFloat(formData.age);
    const height = parseFloat(formData.height);
    const weight = parseFloat(formData.weight);
    const activity = parseFloat(formData.activity);

    if (!age || !height || !weight) return;

    let bmr;
    if (formData.gender === 'male') {
      bmr = (10 * weight) + (6.25 * height) - (5 * age) + 5;
    } else {
      bmr = (10 * weight) + (6.25 * height) - (5 * age) - 161;
    }

    const tdee = bmr * activity;

    let calories, proteinPerKg, fatPerKg;
    switch (formData.goal) {
      case 'aggressive_cut':
        calories = tdee - 600;
        proteinPerKg = 2.4;
        fatPerKg = 0.8;
        break;
      case 'fat_loss':
        calories = tdee - 400;
        proteinPerKg = 2.2;
        fatPerKg = 0.9;
        break;
      case 'maintain':
        calories = tdee;
        proteinPerKg = 2.0;
        fatPerKg = 1.0;
        break;
      case 'muscle_gain':
        calories = tdee + 300;
        proteinPerKg = 2.0;
        fatPerKg = 1.0;
        break;
      default:
        calories = tdee;
        proteinPerKg = 2.0;
        fatPerKg = 1.0;
    }

    const protein = Math.round(weight * proteinPerKg);
    const fat = Math.round(weight * fatPerKg);
    const proteinCals = protein * 4;
    const fatCals = fat * 9;
    const carbCals = calories - proteinCals - fatCals;
    const carbs = Math.round(carbCals / 4);

    setResult({
      calories: Math.round(calories),
      protein,
      carbs: Math.max(0, carbs),
      fat,
      proteinPct: Math.round((proteinCals / calories) * 100),
      carbsPct: Math.round((Math.max(0, carbCals) / calories) * 100),
      fatPct: Math.round((fatCals / calories) * 100),
    });
  };

  const scrollToWaitlist = () => {
    document.querySelector('#waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  const inputClass = "w-full bg-ston-surface/50 border border-ston-border rounded-xl px-4 py-3.5 text-sm text-ston-text placeholder:text-ston-text-muted/50 focus:border-ston-purple focus:ring-2 focus:ring-ston-purple/20 focus:bg-ston-surface transition-all duration-300";
  const selectClass = "w-full bg-ston-surface/50 border border-ston-border rounded-xl px-4 py-3.5 text-sm text-ston-text focus:border-ston-purple focus:ring-2 focus:ring-ston-purple/20 focus:bg-ston-surface transition-all duration-300 appearance-none cursor-pointer";

  return (
    <div data-testid="macro-calculator">
      <motion.div
        layout
        className={`grid gap-8 lg:gap-12 transition-all duration-500 ${result ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-xl mx-auto'
          }`}
      >
        {/* Left - Form */}
        <motion.div layout="position">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Drumstick className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-ston-text">
                Macro Calculator
              </h3>
              <p className="text-sm text-ston-text-muted">Fuel your gains</p>
            </div>
          </div>
          <p className="text-sm text-ston-text-secondary mb-6">
            Your calories and macros, personalized to your goal. No generic plans.
          </p>

          <div className="space-y-4">
            {/* Gender and Age Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className={selectClass}
                  data-testid="macro-gender-select"
                >
                  <option value="male">♂ Male</option>
                  <option value="female">♀ Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Age
                </label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="25"
                  className={inputClass}
                  data-testid="macro-age-input"
                />
              </div>
            </div>

            {/* Height and Weight Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Height
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="175"
                    className={inputClass}
                    data-testid="macro-height-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-ston-text-muted bg-ston-surface/80 px-2 py-0.5 rounded-md">cm</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                  Weight
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="75"
                    className={inputClass}
                    data-testid="macro-weight-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-ston-text-muted bg-ston-surface/80 px-2 py-0.5 rounded-md">kg</span>
                </div>
              </div>
            </div>

            {/* Activity Level */}
            <div>
              <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                Activity Level
              </label>
              <select
                name="activity"
                value={formData.activity}
                onChange={handleChange}
                className={selectClass}
                data-testid="macro-activity-select"
              >
                <option value="1.2">Sedentary (desk job)</option>
                <option value="1.375">Light (1-3 days/week)</option>
                <option value="1.55">Moderate (3-5 days/week)</option>
                <option value="1.725">Active (6-7 days/week)</option>
                <option value="1.9">Very Active (2x/day)</option>
              </select>
            </div>

            {/* Goal */}
            <div>
              <label className="block text-sm font-medium text-ston-text-secondary mb-2.5">
                Goal
              </label>
              <select
                name="goal"
                value={formData.goal}
                onChange={handleChange}
                className={selectClass}
                data-testid="macro-goal-select"
              >
                <option value="aggressive_cut">🔥 Aggressive Cut</option>
                <option value="fat_loss">📉 Fat Loss</option>
                <option value="maintain">⚖️ Maintain</option>
                <option value="muscle_gain">💪 Muscle Gain</option>
              </select>
            </div>

            {/* Calculate Button */}
            <motion.button
              onClick={calculateMacros}
              className="w-full mt-1 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white rounded-xl px-6 py-4 text-base font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-violet-500/30 relative overflow-hidden group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="macro-calculate-btn"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <span className="relative flex items-center justify-center gap-2">
                {result ? 'Recalculate' : 'Calculate'}
                <Zap className="w-4 h-4" />
              </span>
            </motion.button>
          </div>
        </motion.div>

        {/* Right - Result */}
        <AnimatePresence>
          {result && (
            <motion.div
              className="flex flex-col items-center justify-center"
              initial={{ opacity: 0, x: 60, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 60, scale: 0.9 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center w-full">
                {/* Calories Value */}
                <motion.div
                  className="text-6xl md:text-7xl font-extrabold gradient-text mb-1"
                  style={{ letterSpacing: '-3px' }}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.3 }}
                  data-testid="macro-calories-value"
                >
                  {result.calories}
                </motion.div>
                <motion.div
                  className="text-base text-ston-text-secondary mb-8 font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  Daily Calories
                </motion.div>

                {/* Macro Breakdown Cards */}
                <motion.div
                  className="grid grid-cols-3 gap-3 mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  {[
                    { label: 'Protein', value: result.protein, pct: result.proteinPct, color: 'from-purple-500 to-violet-500' },
                    { label: 'Carbs', value: result.carbs, pct: result.carbsPct, color: 'from-violet-500 to-fuchsia-500' },
                    { label: 'Fat', value: result.fat, pct: result.fatPct, color: 'from-fuchsia-500 to-pink-500' },
                  ].map((macro, i) => (
                    <motion.div
                      key={macro.label}
                      className="relative bg-ston-surface/50 rounded-xl p-4 border border-ston-border overflow-hidden"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + i * 0.1 }}
                    >
                      {/* Progress bar at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-ston-border">
                        <motion.div
                          className={`h-full bg-gradient-to-r ${macro.color} rounded-full`}
                          initial={{ width: 0 }}
                          animate={{ width: `${macro.pct}%` }}
                          transition={{ delay: 0.8 + i * 0.1, duration: 0.8 }}
                        />
                      </div>
                      <div className="text-2xl font-bold text-ston-purple-light" data-testid={`macro-${macro.label.toLowerCase()}-value`}>
                        {macro.value}g
                      </div>
                      <div className="text-xs text-ston-text-muted mt-1">{macro.label} · {macro.pct}%</div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Note */}
                <motion.p
                  className="text-sm text-ston-text-muted max-w-[380px] mx-auto mb-6 flex items-start gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-violet-500/50" />
                  <span>Calculated using the Mifflin-St Jeor equation. STON.FIT refines these daily based on your real progress.</span>
                </motion.p>

                {/* CTA */}
                <motion.button
                  onClick={scrollToWaitlist}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="macro-cta-btn"
                >
                  Get Adaptive Nutrition Plan
                  <ArrowRight size={16} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default MacroCalculator;
